import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getPaymentStatus, createCheckoutSession } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Scale, CheckCircle2, Loader2, CreditCard } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [checking, setChecking] = useState(true);
  const [status, setStatus] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setChecking(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setChecking(false);
      setStatus('timeout');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const res = await getPaymentStatus(sid);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        setChecking(false);
        // Update user subscription status
        if (user) {
          updateUser({ ...user, subscription_status: 'active' });
        }
        toast.success('Payment successful! Welcome to Legal AIDynamics');
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        setChecking(false);
        toast.error('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  const handleRetry = async () => {
    try {
      const res = await createCheckoutSession(window.location.origin);
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to create checkout session');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="payment-success-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-md bg-navy flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
          </div>
          <CardTitle className="font-heading text-2xl">
            {checking ? 'Verifying Payment...' : status === 'success' ? 'Payment Successful!' : 'Payment Issue'}
          </CardTitle>
          <CardDescription className="font-body">
            {checking ? 'Please wait while we confirm your payment' : 
             status === 'success' ? 'Your subscription is now active' : 
             'There was an issue with your payment'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {checking ? (
            <div className="py-8">
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-bronze" />
              <p className="mt-4 text-muted-foreground font-body">Processing your payment...</p>
            </div>
          ) : status === 'success' ? (
            <>
              <div className="py-4">
                <CheckCircle2 className="w-16 h-16 mx-auto text-success" />
              </div>
              <div className="space-y-2">
                <p className="font-body text-foreground">Thank you for subscribing!</p>
                <p className="font-body text-sm text-muted-foreground">
                  You now have full access to all Legal AIDynamics features.
                </p>
              </div>
              <Button onClick={handleContinue} className="w-full bg-navy hover:bg-navy-light" data-testid="continue-btn">
                Continue to Dashboard
              </Button>
            </>
          ) : (
            <>
              <div className="py-4">
                <CreditCard className="w-16 h-16 mx-auto text-muted-foreground" />
              </div>
              <div className="space-y-2">
                <p className="font-body text-foreground">Payment could not be verified</p>
                <p className="font-body text-sm text-muted-foreground">
                  Please try again or contact support if the issue persists.
                </p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate('/login')} className="flex-1">
                  Back to Login
                </Button>
                <Button onClick={handleRetry} className="flex-1 bg-bronze hover:bg-bronze-light">
                  Try Again
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
